// ⚙️ Hook para gestionar preferencias de usuario sincronizadas con backend
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { useThemeStore } from '@/stores/theme.store';
import { useTranslation } from './useTranslation';
import type { SupportedLanguages } from '../i18n';

interface UserPreferences {
  theme: string;
  language: SupportedLanguages;
}

interface UpdatePreferencesParams {
  theme?: string;
  language?: SupportedLanguages;
}

/**
 * Obtener preferencias del usuario desde el backend
 */
async function fetchUserPreferences(): Promise<UserPreferences> {
  const { data } = await apiClient.post<{ preferences: UserPreferences }>('/rpc/get_user_preferences', {});
  return data.preferences;
}

/**
 * Actualizar preferencias del usuario en el backend
 */
async function updateUserPreferences(preferences: UpdatePreferencesParams): Promise<UserPreferences> {
  const { data } = await apiClient.post<{ preferences: UserPreferences }>('/rpc/set_user_preferences', {
    _prefs: preferences,
  });
  return data.preferences;
}

/**
 * Hook para gestionar preferencias de usuario con sincronización automática
 */
export function useUserPreferences() {
  const queryClient = useQueryClient();
  const { setTheme } = useThemeStore();
  const { changeLanguage } = useTranslation();

  // Query para obtener preferencias
  const { data: preferences, isLoading } = useQuery({
    queryKey: ['userPreferences'],
    queryFn: fetchUserPreferences,
    retry: false, // No reintentar si falla (probablemente no autenticado)
    staleTime: 5 * 60 * 1000, // 5 minutos
    enabled: true, // Solo habilitar si hay usuario autenticado
  });

  // Mutation para actualizar preferencias
  const updateMutation = useMutation({
    mutationFn: updateUserPreferences,
    onSuccess: (data) => {
      // Actualizar cache
      queryClient.setQueryData(['userPreferences'], data);

      // Aplicar cambios localmente
      if (data.theme) {
        setTheme(data.theme);
      }
      if (data.language) {
        changeLanguage(data.language);
      }
    },
  });

  return {
    preferences,
    isLoading,
    updatePreferences: updateMutation.mutate,
    isUpdating: updateMutation.isPending,
  };
}
